import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BookOpen, Plus, Trash2, Search, Calendar, Download, Upload,
  Camera, FileText, TrendingUp, TrendingDown, Scale, Receipt,
  ArrowRight, Check, Loader2, Eye, QrCode, Video, Smartphone, X, RefreshCw, CheckCircle
} from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  loyers: 'Loyers', assurances: 'Assurances', electricite: 'Electricite',
  telephone: 'Telephone', fournitures: 'Fournitures', pieces_detachees: 'Pieces detachees',
  outillage: 'Outillage', entretien: 'Entretien', carburant: 'Carburant',
  publicite: 'Publicite', formation: 'Formation', honoraires: 'Honoraires',
  sous_traitance: 'Sous-traitance', informatique: 'Informatique', divers: 'Divers',
};

const ComptaPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('journal');
  const tabs = [
    { key: 'journal', label: 'Journal', icon: BookOpen },
    { key: 'scan', label: 'Scan facture', icon: Camera },
    { key: 'plan', label: 'Plan comptable', icon: FileText },
    { key: 'balance', label: 'Balance', icon: Scale },
    { key: 'bilan', label: 'Bilan', icon: TrendingUp },
    { key: 'resultat', label: 'Resultat', icon: TrendingDown },
    { key: 'tva', label: 'TVA', icon: Receipt },
  ];

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="space-y-5 max-w-[1200px] mx-auto" data-testid="compta-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Vortekio Comptabilite</h1>
          <p className="text-sm text-slate-500 mt-0.5">Comptabilite generale, journal, bilan et rapports</p>
        </div>
        <div className="flex bg-slate-100 dark:bg-slate-800 rounded-xl p-1 gap-0.5 overflow-x-auto" data-testid="compta-tabs">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`py-2 px-3 text-xs font-medium rounded-lg transition-all flex items-center gap-1 whitespace-nowrap ${tab === t.key ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${t.key}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
        {tab === 'journal' && <JournalTab />}
        {tab === 'scan' && <ScanTab />}
        {tab === 'plan' && <PlanTab />}
        {tab === 'balance' && <BalanceTab />}
        {tab === 'bilan' && <BilanTab />}
        {tab === 'resultat' && <ResultatTab />}
        {tab === 'tva' && <TvaTab />}
      </div>
    </AdminLayout>
  );
};

/* ═══════════ JOURNAL TAB ═══════════ */
const JournalTab = () => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), label: '', debit_account: '', credit_account: '', amount: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let q = [];
      if (dateFrom) q.push(`date_from=${dateFrom}`);
      if (dateTo) q.push(`date_to=${dateTo}`);
      const [jRes, aRes] = await Promise.all([
        axios.get(`${API}/api/compta/journal${q.length ? '?' + q.join('&') : ''}`),
        axios.get(`${API}/api/compta/accounts`),
      ]);
      setEntries(jRes.data);
      setAccounts(aRes.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    try {
      await axios.post(`${API}/api/compta/journal`, { ...form, amount: parseFloat(form.amount) });
      toast.success("Ecriture enregistree");
      setShowAdd(false);
      setForm({ date: new Date().toISOString().slice(0, 10), label: '', debit_account: '', credit_account: '', amount: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette ecriture ?")) return;
    try { await axios.delete(`${API}/api/compta/journal/${id}`); toast.success("Supprimee"); load(); }
    catch { toast.error("Erreur"); }
  };

  const exportCSV = () => {
    const rows = [['Date', 'Libelle', 'Debit', 'Compte Debit', 'Credit', 'Compte Credit', 'Montant', 'Source', 'Piece']];
    entries.forEach(e => rows.push([e.date, e.label, e.debit_account, e.debit_account_name, e.credit_account, e.credit_account_name, e.amount.toFixed(2), e.source, e.piece_ref]));
    const csv = rows.map(r => r.map(c => `"${c}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `journal_${dateFrom || 'all'}_${dateTo || 'all'}.csv`; a.click();
  };

  const totalDebit = entries.reduce((s, e) => s + e.amount, 0);

  return (
    <div className="space-y-4" data-testid="journal-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <Calendar className="w-4 h-4 text-slate-400" />
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36" data-testid="j-date-from" />
        <span className="text-slate-400">—</span>
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36" data-testid="j-date-to" />
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={exportCSV} data-testid="j-export"><Download className="w-4 h-4 mr-1" /> CSV</Button>
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="j-add"><Plus className="w-4 h-4 mr-1" /> Ecriture</Button>
        </div>
      </div>

      {loading ? <div className="text-center py-12 text-slate-400">{t('common.loading')}</div> : entries.length === 0 ? (
        <Card className="p-12 text-center">
          <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-600">Aucune ecriture comptable</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-left">
              <th className="py-2 px-3 font-semibold text-slate-600 w-24">Date</th>
              <th className="py-2 px-3 font-semibold text-slate-600">Libelle</th>
              <th className="py-2 px-3 font-semibold text-slate-600">Debit</th>
              <th className="py-2 px-3 font-semibold text-slate-600">Credit</th>
              <th className="py-2 px-3 font-semibold text-slate-600 text-right">Montant</th>
              <th className="py-2 px-3 font-semibold text-slate-600 w-16">Src</th>
              <th className="py-2 px-3 w-10"></th>
            </tr></thead>
            <tbody className="divide-y">
              {entries.map(e => (
                <tr key={e.id} className="hover:bg-slate-50" data-testid={`je-${e.id}`}>
                  <td className="py-1.5 px-3 text-slate-600 text-xs">{e.date}</td>
                  <td className="py-1.5 px-3 text-slate-800 font-medium text-xs">{e.label}</td>
                  <td className="py-1.5 px-3 text-xs"><span className="font-mono text-red-700">{e.debit_account}</span> <span className="text-slate-400">{e.debit_account_name}</span></td>
                  <td className="py-1.5 px-3 text-xs"><span className="font-mono text-emerald-700">{e.credit_account}</span> <span className="text-slate-400">{e.credit_account_name}</span></td>
                  <td className="py-1.5 px-3 text-right font-semibold text-slate-800 text-xs">CHF {e.amount.toFixed(2)}</td>
                  <td className="py-1.5 px-3"><Badge className="text-[10px] bg-slate-100 text-slate-600">{e.source}</Badge></td>
                  <td className="py-1.5 px-3">{e.source === 'manual' && <button onClick={() => handleDelete(e.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="bg-slate-50 font-bold text-xs">
              <td colSpan={4} className="py-2 px-3 text-slate-700">Total mouvements</td>
              <td className="py-2 px-3 text-right text-slate-700">CHF {totalDebit.toFixed(2)}</td>
              <td colSpan={2}></td>
            </tr></tfoot>
          </table>
        </Card>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nouvelle ecriture</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="je-date" /></div>
              <div><Label className="text-xs">Montant CHF</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="je-amount" /></div>
            </div>
            <div><Label className="text-xs">Libelle</Label><Input value={form.label} onChange={e => setForm({...form, label: e.target.value})} data-testid="je-label" /></div>
            <div><Label className="text-xs">Compte au debit</Label>
              <Select value={form.debit_account} onValueChange={v => setForm({...form, debit_account: v})}>
                <SelectTrigger data-testid="je-debit"><SelectValue placeholder="Selectionner..." /></SelectTrigger>
                <SelectContent className="max-h-48">{accounts.map(a => <SelectItem key={a.number} value={a.number}>{a.number} - {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Compte au credit</Label>
              <Select value={form.credit_account} onValueChange={v => setForm({...form, credit_account: v})}>
                <SelectTrigger data-testid="je-credit"><SelectValue placeholder="Selectionner..." /></SelectTrigger>
                <SelectContent className="max-h-48">{accounts.map(a => <SelectItem key={a.number} value={a.number}>{a.number} - {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={handleAdd} className="w-full" disabled={!form.debit_account || !form.credit_account || !form.amount} data-testid="je-save">{t('common.save')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ═══════════ SCAN TAB ═══════════ */
const ScanTab = () => {
  const [files, setFiles] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [scannedList, setScannedList] = useState([]);
  const [scanMode, setScanMode] = useState(null);
  // QR state
  const [qrSession, setQrSession] = useState(null);
  const [qrPolling, setQrPolling] = useState(false);
  const [qrFileCount, setQrFileCount] = useState(0);
  // Webcam state
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [webcamActive, setWebcamActive] = useState(false);
  const [webcamError, setWebcamError] = useState('');
  const pollingRef = useRef(null);

  useEffect(() => {
    (async () => {
      const [aRes, sRes] = await Promise.all([
        axios.get(`${API}/api/compta/accounts`),
        axios.get(`${API}/api/compta/scanned-invoices`),
      ]);
      setAccounts(aRes.data);
      setScannedList(sRes.data);
    })();
  }, []);

  useEffect(() => {
    return () => {
      stopWebcam();
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // ─── Add file to list ───
  const addFile = (f) => {
    if (!f) return;
    setFiles(prev => [...prev, f]);
  };
  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // ─── MULTI-FILE UPLOAD & SCAN ───
  const handleScanAll = async (filesToScan) => {
    const list = filesToScan || files;
    if (list.length === 0) return;
    setScanning(true); setResult(null);
    try {
      const fd = new FormData();
      list.forEach(f => fd.append('files', f));
      const res = await axios.post(`${API}/api/compta/scan-invoice`, fd, { timeout: 120000 });
      setResult(res.data);
      setSelectedAccount(res.data.suggested_account || '6700');
      setSelectedCategory(res.data.category || 'divers');
      if (res.data.ocr_error) toast.error("Erreur OCR: " + res.data.ocr_error);
      else toast.success(`Facture analysee (${res.data.page_count || 1} page${(res.data.page_count || 1) > 1 ? 's' : ''})`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur scan"); }
    finally { setScanning(false); }
  };

  // ─── QR CODE FLOW (multi-page) ───
  const startQrSession = async () => {
    try {
      const res = await axios.post(`${API}/api/compta/scan-session`);
      const uploadUrl = `${window.location.origin}/compta-scan/${res.data.session_id}`;
      setQrSession({ ...res.data, upload_url: uploadUrl });
      setQrPolling(true);
      setQrFileCount(0);
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = setInterval(async () => {
        try {
          const poll = await axios.get(`${API}/api/compta/scan-session/${res.data.session_id}/status`);
          setQrFileCount(poll.data.file_count || 0);
        } catch { /* ignore */ }
      }, 2500);
    } catch { toast.error("Erreur creation session"); }
  };

  const processQrSession = async () => {
    if (!qrSession) return;
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    setQrPolling(false);
    setScanning(true);
    try {
      const ocrRes = await axios.post(`${API}/api/compta/scan-session/${qrSession.session_id}/process`, {}, { timeout: 120000 });
      setResult(ocrRes.data);
      setSelectedAccount(ocrRes.data.suggested_account || '6700');
      setSelectedCategory(ocrRes.data.category || 'divers');
      if (ocrRes.data.ocr_error) toast.error("Erreur OCR: " + ocrRes.data.ocr_error);
      else toast.success(`Facture analysee (${ocrRes.data.page_count || 1} page${(ocrRes.data.page_count || 1) > 1 ? 's' : ''})`);
      setScanMode(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur analyse"); }
    finally { setScanning(false); }
  };

  const stopQrSession = () => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    setQrSession(null);
    setQrPolling(false);
    setQrFileCount(0);
  };

  // ─── WEBCAM FLOW ───
  const startWebcam = async () => {
    setWebcamError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setWebcamActive(true);
    } catch {
      setWebcamError("Impossible d'acceder a la webcam. Verifiez les permissions.");
    }
  };

  const stopWebcam = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
    setWebcamActive(false);
  };

  const captureWebcam = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const f = new File([blob], `webcam_${Date.now()}.jpg`, { type: 'image/jpeg' });
      addFile(f);
      toast.success(`Page ${files.length + 1} capturee`);
    }, 'image/jpeg', 0.9);
  };

  // ─── CONFIRM ───
  const handleConfirm = async () => {
    if (!result?.extracted) return;
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/api/compta/confirm-scanned-invoice`, {
        extracted: result.extracted,
        account_number: selectedAccount,
        category: selectedCategory,
        storage_path: result.storage_path,
        storage_paths: result.storage_paths,
        page_count: result.page_count,
        original_filename: result.original_filename,
      });
      toast.success(`${res.data.entries_created} ecriture(s) creee(s) pour ${res.data.supplier}`);
      setResult(null); setFiles([]);
      const sRes = await axios.get(`${API}/api/compta/scanned-invoices`);
      setScannedList(sRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setConfirming(false); }
  };

  const ext = result?.extracted;
  const hasFiles = files.length > 0;

  return (
    <div className="space-y-5" data-testid="scan-tab">
      {/* Mode Selection */}
      {!scanning && !ext && (
        <Card className="p-6">
          <div className="text-center mb-5">
            <Camera className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-800 mb-1">Scanner une facture fournisseur</h3>
            <p className="text-sm text-slate-500">Plusieurs pages ? Ajoutez chaque page puis lancez l'analyse</p>
          </div>

          {/* 3 Mode Buttons */}
          <div className="grid grid-cols-3 gap-3 max-w-lg mx-auto mb-4">
            <button onClick={() => { setScanMode('qr'); stopWebcam(); startQrSession(); }}
              className={`flex flex-col items-center gap-2 p-4 border-2 rounded-xl transition-all cursor-pointer ${scanMode === 'qr' ? 'border-emerald-500 bg-emerald-50' : 'border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/50'}`}
              data-testid="mode-qr-btn">
              <Smartphone className="w-8 h-8 text-emerald-600" />
              <span className="text-xs font-medium text-slate-700">QR Telephone</span>
            </button>
            <button onClick={() => { setScanMode('webcam'); stopQrSession(); startWebcam(); }}
              className={`flex flex-col items-center gap-2 p-4 border-2 rounded-xl transition-all cursor-pointer ${scanMode === 'webcam' ? 'border-blue-500 bg-blue-50' : 'border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/50'}`}
              data-testid="mode-webcam-btn">
              <Video className="w-8 h-8 text-blue-600" />
              <span className="text-xs font-medium text-slate-700">Webcam</span>
            </button>
            <label className={`flex flex-col items-center gap-2 p-4 border-2 rounded-xl transition-all cursor-pointer ${scanMode === 'file' ? 'border-violet-500 bg-violet-50' : 'border-dashed border-slate-300 hover:border-violet-400 hover:bg-violet-50/50'}`}
              data-testid="mode-file-btn">
              <Upload className="w-8 h-8 text-violet-600" />
              <span className="text-xs font-medium text-slate-700">Fichier</span>
              <input type="file" accept="image/*,.pdf,application/pdf" multiple className="hidden"
                onChange={e => {
                  setScanMode('file'); stopQrSession(); stopWebcam();
                  Array.from(e.target.files || []).forEach(f => addFile(f));
                  e.target.value = '';
                }} />
            </label>
          </div>

          {/* QR Code Panel */}
          {scanMode === 'qr' && qrSession && (
            <div className="max-w-sm mx-auto text-center space-y-3 p-4 bg-white border border-emerald-200 rounded-xl" data-testid="qr-panel">
              <p className="text-sm font-medium text-slate-700">Scannez ce QR code avec votre telephone</p>
              <div className="bg-white p-4 rounded-lg inline-block border">
                <QRCodeSVG value={qrSession.upload_url} size={180} level="M" />
              </div>
              {qrFileCount > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2 text-emerald-600" data-testid="qr-received-count">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-bold">{qrFileCount} page{qrFileCount > 1 ? 's' : ''} recu{qrFileCount > 1 ? 'es' : 'e'}</span>
                  </div>
                  <p className="text-xs text-slate-400">Prenez d'autres photos ou lancez l'analyse</p>
                  <Button onClick={processQrSession} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="qr-analyze-btn">
                    <Search className="w-4 h-4 mr-2" /> Analyser {qrFileCount} page{qrFileCount > 1 ? 's' : ''}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 text-emerald-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">En attente de la photo...</span>
                </div>
              )}
              <p className="text-xs text-slate-400">Plusieurs pages ? Prenez chaque page sur votre telephone</p>
              <Button size="sm" variant="ghost" onClick={() => { stopQrSession(); setScanMode(null); }} className="text-xs text-slate-400">
                <X className="w-3 h-3 mr-1" /> Annuler
              </Button>
            </div>
          )}

          {/* Webcam Panel */}
          {scanMode === 'webcam' && (
            <div className="max-w-lg mx-auto space-y-3" data-testid="webcam-panel">
              {webcamError ? (
                <div className="text-center p-4 bg-red-50 rounded-xl">
                  <p className="text-sm text-red-600">{webcamError}</p>
                  <Button size="sm" variant="outline" className="mt-2" onClick={startWebcam}>
                    <RefreshCw className="w-3 h-3 mr-1" /> Reessayer
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                    <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                    {!webcamActive && (
                      <div className="absolute inset-0 flex items-center justify-center bg-slate-900/60">
                        <Loader2 className="w-8 h-8 animate-spin text-white" />
                      </div>
                    )}
                  </div>
                  <canvas ref={canvasRef} className="hidden" />
                  <div className="flex gap-2 justify-center">
                    <Button onClick={captureWebcam} disabled={!webcamActive} className="bg-blue-600 hover:bg-blue-700" data-testid="webcam-capture-btn">
                      <Camera className="w-4 h-4 mr-2" /> Capturer page {files.length + 1}
                    </Button>
                    <Button variant="outline" onClick={() => { stopWebcam(); setScanMode(null); }}>
                      <X className="w-4 h-4 mr-1" /> Fermer
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Files List (file mode + webcam captures) */}
          {hasFiles && scanMode !== 'qr' && (
            <div className="max-w-lg mx-auto space-y-3 mt-4" data-testid="files-list">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-700">{files.length} page{files.length > 1 ? 's' : ''} selectionnee{files.length > 1 ? 's' : ''}</p>
                {scanMode === 'file' && (
                  <label className="text-xs text-violet-600 font-medium cursor-pointer hover:underline flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Ajouter
                    <input type="file" accept="image/*,.pdf,application/pdf" multiple className="hidden"
                      onChange={e => { Array.from(e.target.files || []).forEach(f => addFile(f)); e.target.value = ''; }} />
                  </label>
                )}
              </div>
              <div className="space-y-1.5">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg" data-testid={`file-item-${i}`}>
                    <div className="w-8 h-8 rounded bg-violet-100 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-violet-600">{i + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700 truncate">{f.name}</p>
                      <p className="text-[10px] text-slate-400">{(f.size / 1024).toFixed(0)} Ko</p>
                    </div>
                    <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500 shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <Button onClick={() => handleScanAll()} disabled={scanning} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="scan-all-btn">
                {scanning ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Analyse...</> : <><Search className="w-4 h-4 mr-1" /> Analyser {files.length} page{files.length > 1 ? 's' : ''}</>}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Scanning indicator */}
      {scanning && !ext && (
        <Card className="p-8 text-center">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-500 mx-auto mb-3" />
          <p className="font-semibold text-slate-700">Analyse IA en cours...</p>
          <p className="text-sm text-slate-500 mt-1">L'IA combine les donnees de toutes les pages</p>
        </Card>
      )}

      {/* OCR Result */}
      {ext && (
        <Card className="p-5 border-blue-200 bg-blue-50/30" data-testid="scan-result">
          <h4 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
            <Check className="w-5 h-5" /> Donnees extraites
            {result?.page_count > 1 && <Badge className="bg-blue-100 text-blue-700 text-[10px] ml-2">{result.page_count} pages</Badge>}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <p><span className="text-slate-500">Fournisseur:</span> <strong>{ext.supplier_name}</strong></p>
              <p><span className="text-slate-500">Adresse:</span> {ext.supplier_address || '-'}</p>
              <p><span className="text-slate-500">IBAN:</span> <span className="font-mono text-xs">{ext.supplier_iban || '-'}</span></p>
              <p><span className="text-slate-500">N facture:</span> <strong>{ext.invoice_number || '-'}</strong></p>
              <p><span className="text-slate-500">Date:</span> {ext.invoice_date || '-'}</p>
            </div>
            <div className="space-y-2">
              <p><span className="text-slate-500">Sous-total:</span> CHF {(ext.subtotal || 0).toFixed(2)}</p>
              <p><span className="text-slate-500">TVA ({ext.vat_rate || 8.1}%):</span> CHF {(ext.vat_amount || 0).toFixed(2)}</p>
              <p className="text-lg"><span className="text-slate-500">Total:</span> <strong className="text-slate-900">CHF {(ext.total || 0).toFixed(2)}</strong></p>
              {ext.payment_reference && <p><span className="text-slate-500">Ref paiement:</span> <span className="font-mono text-xs">{ext.payment_reference}</span></p>}
            </div>
          </div>
          {ext.lines?.length > 0 && (
            <div className="mt-3 border-t pt-3">
              <p className="text-xs font-semibold text-slate-600 mb-1">Lignes ({ext.lines.length})</p>
              {ext.lines.map((l, i) => (
                <p key={i} className="text-xs text-slate-500">{l.description} — Qty: {l.quantity} x CHF {(l.unit_price || 0).toFixed(2)} = CHF {(l.total || 0).toFixed(2)}</p>
              ))}
            </div>
          )}
          <div className="mt-4 pt-4 border-t space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Categorie</Label>
                <Select value={selectedCategory} onValueChange={v => {
                  setSelectedCategory(v);
                  axios.get(`${API}/api/compta/category-accounts`).then(r => {
                    if (r.data[v]) setSelectedAccount(r.data[v]);
                  });
                }}>
                  <SelectTrigger data-testid="scan-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Compte de charge</Label>
                <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                  <SelectTrigger data-testid="scan-account"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-48">
                    {accounts.filter(a => a.class_num >= 4 && a.class_num <= 7).map(a => (
                      <SelectItem key={a.number} value={a.number}>{a.number} - {a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleConfirm} disabled={confirming} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-scan">
                {confirming ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Valider et creer les ecritures
              </Button>
              <Button variant="outline" onClick={() => { setResult(null); setFiles([]); }} data-testid="scan-reset">
                <RefreshCw className="w-4 h-4 mr-1" /> Nouvelle
              </Button>
            </div>
          </div>
        </Card>
      )}

      {scannedList.length > 0 && (
        <Card className="overflow-hidden">
          <div className="p-3 border-b bg-slate-50"><p className="text-sm font-semibold text-slate-700">Factures scannees</p></div>
          <div className="divide-y max-h-72 overflow-y-auto">
            {scannedList.map(s => (
              <InvoiceRow key={s.id} invoice={s} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

/* ═══════════ INVOICE ROW + DETAIL DIALOG ═══════════ */
const InvoiceRow = ({ invoice }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const openDetail = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/compta/scanned-invoices/${invoice.id}`);
      setDetail(res.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  const paths = detail?.storage_paths || (detail?.storage_path ? [detail.storage_path] : []);
  const entries = detail?.journal_entries || [];
  const d = detail || invoice;

  return (
    <>
      <div onClick={openDetail}
        className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors"
        data-testid={`scanned-${invoice.id}`}>
        <div className="flex items-center gap-3">
          {invoice.storage_path && (
            <div className="w-9 h-9 rounded-lg bg-slate-100 overflow-hidden shrink-0">
              <img src={`${API}/api/compta/files/${invoice.storage_path}`} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-slate-800">
              {invoice.supplier_name}
              {(invoice.page_count || 1) > 1 && <span className="ml-1.5 text-[10px] text-slate-400">({invoice.page_count} pages)</span>}
            </p>
            <p className="text-xs text-slate-500">{invoice.invoice_number || 'Sans num.'} | {invoice.invoice_date} | {CATEGORY_LABELS[invoice.category] || invoice.category}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-sm font-semibold text-slate-800">CHF {(invoice.total || 0).toFixed(2)}</p>
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{invoice.account_number}</Badge>
          </div>
          <Eye className="w-4 h-4 text-slate-400" />
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="invoice-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-600" />
              {d.supplier_name}
              {(d.page_count || 1) > 1 && <Badge className="bg-blue-100 text-blue-700 text-xs">{d.page_count} pages</Badge>}
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
            </div>
          ) : (
            <div className="space-y-5">
              {/* Images */}
              {paths.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Documents ({paths.length})</p>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {paths.map((p, i) => (
                      <div key={i} onClick={() => setLightbox(p)}
                        className="shrink-0 w-32 h-40 rounded-lg border-2 border-slate-200 overflow-hidden cursor-pointer hover:border-emerald-400 transition-colors relative group"
                        data-testid={`invoice-page-${i}`}>
                        <img src={`${API}/api/compta/files/${p}`} alt={`Page ${i + 1}`} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                          {i + 1}/{paths.length}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Extracted Data */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Donnees extraites</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm bg-slate-50 rounded-lg p-4">
                  <p><span className="text-slate-400">Fournisseur:</span> <strong className="text-slate-800">{d.supplier_name}</strong></p>
                  <p><span className="text-slate-400">N facture:</span> <strong className="text-slate-800">{d.invoice_number || '-'}</strong></p>
                  <p><span className="text-slate-400">Adresse:</span> <span className="text-slate-700">{d.supplier_address || '-'}</span></p>
                  <p><span className="text-slate-400">Date:</span> <span className="text-slate-700">{d.invoice_date}</span></p>
                  <p><span className="text-slate-400">IBAN:</span> <span className="font-mono text-xs text-slate-700">{d.supplier_iban || '-'}</span></p>
                  <p><span className="text-slate-400">Echeance:</span> <span className="text-slate-700">{d.due_date || '-'}</span></p>
                  <p><span className="text-slate-400">Ref paiement:</span> <span className="font-mono text-xs text-slate-700">{d.payment_reference || '-'}</span></p>
                  <p><span className="text-slate-400">Categorie:</span> <span className="text-slate-700">{CATEGORY_LABELS[d.category] || d.category}</span></p>
                </div>
                {/* Lines */}
                {d.lines?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-slate-500 mb-1">Lignes de la facture</p>
                    <div className="text-xs border rounded-lg overflow-hidden">
                      <div className="grid grid-cols-[1fr_60px_80px_80px] gap-2 px-3 py-1.5 bg-slate-100 font-semibold text-slate-600">
                        <span>{t('common.description')}</span><span className="text-right">Qty</span><span className="text-right">P.U.</span><span className="text-right">Total</span>
                      </div>
                      {d.lines.map((l, i) => (
                        <div key={i} className="grid grid-cols-[1fr_60px_80px_80px] gap-2 px-3 py-1.5 border-t text-slate-700">
                          <span className="truncate">{l.description}</span>
                          <span className="text-right">{l.quantity}</span>
                          <span className="text-right">{(l.unit_price || 0).toFixed(2)}</span>
                          <span className="text-right font-medium">{(l.total || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Totals */}
                <div className="flex justify-end mt-3">
                  <div className="text-sm space-y-0.5 text-right">
                    <p className="text-slate-500">Sous-total: <span className="text-slate-800">CHF {(d.subtotal || 0).toFixed(2)}</span></p>
                    <p className="text-slate-500">TVA ({d.vat_rate || 8.1}%): <span className="text-slate-800">CHF {(d.vat_amount || 0).toFixed(2)}</span></p>
                    <p className="text-base font-bold text-slate-900">Total: CHF {(d.total || 0).toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Journal Entries */}
              {entries.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Ecritures comptables ({entries.length})</p>
                  <div className="text-xs border rounded-lg overflow-hidden">
                    <div className="grid grid-cols-[1fr_100px_100px_80px] gap-2 px-3 py-1.5 bg-slate-100 font-semibold text-slate-600">
                      <span>Libelle</span><span>Debit</span><span>Credit</span><span className="text-right">Montant</span>
                    </div>
                    {entries.map((e, i) => (
                      <div key={i} className="grid grid-cols-[1fr_100px_100px_80px] gap-2 px-3 py-1.5 border-t text-slate-700">
                        <span className="truncate">{e.label}</span>
                        <span className="font-mono text-emerald-700">{e.debit_account}</span>
                        <span className="font-mono text-red-600">{e.credit_account}</span>
                        <span className="text-right font-medium">CHF {(e.amount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="image-lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white/80 hover:text-white">
            <X className="w-8 h-8" />
          </button>
          <img src={`${API}/api/compta/files/${lightbox}`} alt="Facture"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" />
        </div>
      )}
    </>
  );
};

/* ═══════════ PLAN COMPTABLE TAB ═══════════ */
const PlanTab = () => {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ number: '', name: '' });

  useEffect(() => {
    (async () => {
      const res = await axios.get(`${API}/api/compta/accounts`);
      setAccounts(res.data);
    })();
  }, []);

  const handleAdd = async () => {
    try {
      const res = await axios.post(`${API}/api/compta/accounts`, form);
      toast.success(`Compte ${res.data.number} cree`);
      setShowAdd(false); setForm({ number: '', name: '' });
      const r = await axios.get(`${API}/api/compta/accounts`);
      setAccounts(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const filtered = accounts.filter(a => {
    if (!search) return true;
    const q = search.toLowerCase();
    return a.number.includes(q) || (a.name || '').toLowerCase().includes(q);
  });

  const classes = [
    { num: 1, label: 'Actifs', color: 'bg-blue-50 text-blue-700' },
    { num: 2, label: 'Passifs', color: 'bg-violet-50 text-violet-700' },
    { num: 3, label: 'Produits', color: 'bg-emerald-50 text-emerald-700' },
    { num: 4, label: 'Charges materiel', color: 'bg-red-50 text-red-700' },
    { num: 5, label: 'Charges personnel', color: 'bg-orange-50 text-orange-700' },
    { num: 6, label: 'Charges exploitation', color: 'bg-amber-50 text-amber-700' },
    { num: 7, label: 'Charges hors exploit.', color: 'bg-rose-50 text-rose-700' },
    { num: 8, label: 'Resultats exceptionnels', color: 'bg-slate-50 text-slate-700' },
    { num: 9, label: 'Cloture', color: 'bg-gray-50 text-gray-700' },
  ];

  return (
    <div className="space-y-4" data-testid="plan-tab">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un compte..." className="pl-9" data-testid="plan-search" />
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)} data-testid="plan-add"><Plus className="w-4 h-4 mr-1" /> Compte</Button>
      </div>

      {classes.map(cls => {
        const accs = filtered.filter(a => a.class_num === cls.num);
        if (accs.length === 0) return null;
        return (
          <Card key={cls.num} className="overflow-hidden">
            <div className={`p-2.5 border-b ${cls.color} font-semibold text-sm`}>Classe {cls.num} — {cls.label}</div>
            <div className="divide-y">
              {accs.map(a => (
                <div key={a.id || a.number} className="flex items-center justify-between px-4 py-1.5 hover:bg-slate-50" data-testid={`acc-${a.number}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-semibold text-slate-700 w-12">{a.number}</span>
                    <span className="text-sm text-slate-800">{a.name}</span>
                    {a.is_custom && <Badge className="bg-blue-100 text-blue-600 text-[10px]">Perso</Badge>}
                  </div>
                  <Badge className={`text-[10px] ${a.type === 'bilan' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}>{a.type}</Badge>
                </div>
              ))}
            </div>
          </Card>
        );
      })}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nouveau compte</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Numero</Label><Input value={form.number} onChange={e => setForm({...form, number: e.target.value})} placeholder="Ex: 6050" data-testid="new-acc-num" /></div>
            <div><Label className="text-xs">Nom</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Ex: Nettoyage" data-testid="new-acc-name" /></div>
            <Button onClick={handleAdd} className="w-full" disabled={!form.number || !form.name} data-testid="save-acc">{t('common.create')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ═══════════ BALANCE TAB ═══════════ */
const BalanceTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/api/compta/balance`); setData(res.data); }
      catch { toast.error("Erreur"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-center py-12 text-slate-400">{t('common.loading')}</div>;
  const totalDebit = data.reduce((s, b) => s + b.debit_total, 0);
  const totalCredit = data.reduce((s, b) => s + b.credit_total, 0);

  return (
    <div className="space-y-4" data-testid="balance-tab">
      {data.length === 0 ? (
        <Card className="p-12 text-center"><Scale className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-600">Aucun mouvement</p></Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-left">
              <th className="py-2 px-3 font-semibold text-slate-600">Compte</th>
              <th className="py-2 px-3 font-semibold text-slate-600">Nom</th>
              <th className="py-2 px-3 font-semibold text-slate-600 text-right">Debit</th>
              <th className="py-2 px-3 font-semibold text-slate-600 text-right">Credit</th>
              <th className="py-2 px-3 font-semibold text-slate-600 text-right">Solde</th>
            </tr></thead>
            <tbody className="divide-y">
              {data.map(b => (
                <tr key={b.number} className="hover:bg-slate-50" data-testid={`bal-${b.number}`}>
                  <td className="py-1.5 px-3 font-mono font-semibold text-slate-700">{b.number}</td>
                  <td className="py-1.5 px-3 text-slate-800">{b.name}</td>
                  <td className="py-1.5 px-3 text-right text-red-700">{b.debit_total > 0 ? b.debit_total.toFixed(2) : ''}</td>
                  <td className="py-1.5 px-3 text-right text-emerald-700">{b.credit_total > 0 ? b.credit_total.toFixed(2) : ''}</td>
                  <td className={`py-1.5 px-3 text-right font-semibold ${b.balance >= 0 ? 'text-slate-800' : 'text-red-700'}`}>{b.balance.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="bg-slate-100 font-bold text-xs">
              <td colSpan={2} className="py-2 px-3">Total</td>
              <td className="py-2 px-3 text-right text-red-700">{totalDebit.toFixed(2)}</td>
              <td className="py-2 px-3 text-right text-emerald-700">{totalCredit.toFixed(2)}</td>
              <td></td>
            </tr></tfoot>
          </table>
        </Card>
      )}
    </div>
  );
};

/* ═══════════ BILAN TAB ═══════════ */
const BilanTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/api/compta/bilan`); setData(res.data); }
      catch { toast.error("Erreur"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-center py-12 text-slate-400">{t('common.loading')}</div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="bilan-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="overflow-hidden">
          <div className="p-3 border-b bg-blue-50"><p className="font-semibold text-blue-800">Actifs</p></div>
          <div className="divide-y">
            {data.actifs.map(a => (
              <div key={a.number} className="flex justify-between px-4 py-2">
                <span className="text-sm text-slate-700"><span className="font-mono mr-2">{a.number}</span>{a.name}</span>
                <span className="text-sm font-semibold text-slate-800">{a.balance.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="p-3 border-t bg-blue-50 flex justify-between font-bold text-blue-800">
            <span>Total Actifs</span><span data-testid="total-actif">CHF {data.total_actif.toFixed(2)}</span>
          </div>
        </Card>
        <Card className="overflow-hidden">
          <div className="p-3 border-b bg-violet-50"><p className="font-semibold text-violet-800">Passifs + Fonds propres</p></div>
          <div className="divide-y">
            {data.passifs.map(a => (
              <div key={a.number} className="flex justify-between px-4 py-2">
                <span className="text-sm text-slate-700"><span className="font-mono mr-2">{a.number}</span>{a.name}</span>
                <span className="text-sm font-semibold text-slate-800">{a.balance.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between px-4 py-2 bg-amber-50">
              <span className="text-sm text-amber-700 font-medium">Resultat de l'exercice</span>
              <span className={`text-sm font-bold ${data.resultat >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{data.resultat.toFixed(2)}</span>
            </div>
          </div>
          <div className="p-3 border-t bg-violet-50 flex justify-between font-bold text-violet-800">
            <span>Total Passifs</span><span data-testid="total-passif">CHF {data.total_passif.toFixed(2)}</span>
          </div>
        </Card>
      </div>
    </div>
  );
};

/* ═══════════ RESULTAT TAB ═══════════ */
const ResultatTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let q = [];
      if (dateFrom) q.push(`date_from=${dateFrom}`);
      if (dateTo) q.push(`date_to=${dateTo}`);
      const res = await axios.get(`${API}/api/compta/resultat${q.length ? '?' + q.join('&') : ''}`);
      setData(res.data);
    } catch { toast.error("Erreur"); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-12 text-slate-400">{t('common.loading')}</div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="resultat-tab">
      <div className="flex items-center gap-3">
        <Calendar className="w-4 h-4 text-slate-400" />
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36" />
        <span className="text-slate-400">—</span>
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36" />
      </div>

      <Card className={`p-5 text-center ${data.resultat >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
        <p className="text-sm text-slate-600">Resultat de l'exercice</p>
        <p className={`text-3xl font-bold ${data.resultat >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="resultat-net">
          CHF {data.resultat.toFixed(2)}
        </p>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="overflow-hidden">
          <div className="p-3 border-b bg-emerald-50"><p className="font-semibold text-emerald-800">Produits</p></div>
          <div className="divide-y">
            {data.produits.map(p => (
              <div key={p.number} className="flex justify-between px-4 py-2">
                <span className="text-sm text-slate-700"><span className="font-mono mr-2">{p.number}</span>{p.name}</span>
                <span className="text-sm font-semibold text-emerald-700">{p.amount.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="p-3 border-t bg-emerald-50 flex justify-between font-bold text-emerald-800">
            <span>Total Produits</span><span>CHF {data.total_produits.toFixed(2)}</span>
          </div>
        </Card>
        <Card className="overflow-hidden">
          <div className="p-3 border-b bg-red-50"><p className="font-semibold text-red-800">Charges</p></div>
          <div className="divide-y">
            {data.charges.map(c => (
              <div key={c.number} className="flex justify-between px-4 py-2">
                <span className="text-sm text-slate-700"><span className="font-mono mr-2">{c.number}</span>{c.name}</span>
                <span className="text-sm font-semibold text-red-700">{c.amount.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="p-3 border-t bg-red-50 flex justify-between font-bold text-red-800">
            <span>Total Charges</span><span>CHF {data.total_charges.toFixed(2)}</span>
          </div>
        </Card>
      </div>
    </div>
  );
};

/* ═══════════ TVA TAB ═══════════ */
const TvaTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let q = [];
      if (dateFrom) q.push(`date_from=${dateFrom}`);
      if (dateTo) q.push(`date_to=${dateTo}`);
      const res = await axios.get(`${API}/api/compta/tva${q.length ? '?' + q.join('&') : ''}`);
      setData(res.data);
    } catch { toast.error("Erreur"); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-12 text-slate-400">{t('common.loading')}</div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="tva-tab">
      <div className="flex items-center gap-3">
        <Calendar className="w-4 h-4 text-slate-400" />
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36" />
        <span className="text-slate-400">—</span>
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 text-center">
          <p className="text-xs text-slate-500 mb-1">TVA due (ventes)</p>
          <p className="text-xl font-bold text-red-700" data-testid="tva-due">CHF {data.tva_due.toFixed(2)}</p>
          <p className="text-[10px] text-slate-400 mt-1">Compte 2200</p>
        </Card>
        <Card className="p-5 text-center">
          <p className="text-xs text-slate-500 mb-1">TVA recuperable (achats)</p>
          <p className="text-xl font-bold text-emerald-700" data-testid="tva-input">CHF {data.tva_input.toFixed(2)}</p>
          <p className="text-[10px] text-slate-400 mt-1">Compte 1170</p>
        </Card>
        <Card className={`p-5 text-center ${data.tva_balance >= 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
          <p className="text-xs text-slate-500 mb-1">Solde TVA a payer</p>
          <p className={`text-xl font-bold ${data.tva_balance >= 0 ? 'text-red-700' : 'text-emerald-700'}`} data-testid="tva-balance">
            CHF {data.tva_balance.toFixed(2)}
          </p>
          <p className="text-[10px] text-slate-400 mt-1">{data.tva_balance >= 0 ? 'A verser a l\'AFC' : 'Credit TVA en votre faveur'}</p>
        </Card>
      </div>
    </div>
  );
};

export default ComptaPage;
